/**
 * @module oauth2-handler
 * @description OAuth2 authorization flow handler providing popup-based authentication with secure message communication.
 */

export interface OAuth2HandlerOptions {
  /**
   * The authorization URL to open in popup
   */
  authorizationUrl: string
  
  /**
   * Width of the popup window
   * @default 600
   */
  popupWidth?: number
  
  /**
   * Height of the popup window
   * @default 700
   */
  popupHeight?: number
  
  /**
   * Timeout for the OAuth2 flow in milliseconds
   * @default 300000 (5 minutes)
   */
  timeout?: number
  
  /**
   * Callback function called when authorization succeeds
   */
  onSuccess?: (data: unknown) => void
  
  /**
   * Callback function called when authorization fails
   */
  onError?: (error: { type: string; message: string; details?: string }) => void
  
  /**
   * Callback function called when the flow is cancelled/closed
   */
  onCancel?: () => void
}

export interface OAuth2Result {
  success: boolean
  data?: unknown
  error?: {
    type: string
    message: string
    details?: string
  }
}

/**
 * Opens an OAuth2 authorization popup and handles the callback.
 * Manages popup lifecycle, message events, and timeout handling.
 * @param options - Configuration options for the OAuth2 flow
 * @returns Promise that resolves when the OAuth2 flow completes
 */
export function handleOAuth2Flow(options: OAuth2HandlerOptions): Promise<OAuth2Result> {
  const {
    authorizationUrl,
    popupWidth = 600,
    popupHeight = 700,
    timeout = 300000, // 5 minutes
    onSuccess,
    onError,
    onCancel,
  } = options

  return new Promise((resolve) => {
    // Initialize popup window reference
    let authWindow: Window | null = null
    // Initialize timeout reference
    let timeoutId: NodeJS.Timeout | null = null
    // Initialize message listener reference
    let messageListener: ((event: MessageEvent) => void) | null = null

    // Calculate centered popup position
    const screenWidth = window.screen.width
    const screenHeight = window.screen.height
    // Center horizontally
    const left = Math.round((screenWidth - popupWidth) / 2)
    // Center vertically
    const top = Math.round((screenHeight - popupHeight) / 2)

    // Cleanup function for resources
    const cleanup = () => {
      // Clear timeout if exists
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      
      // Remove message listener if exists
      if (messageListener) {
        window.removeEventListener('message', messageListener)
        messageListener = null
      }
      
      // Close popup if still open
      if (authWindow && !authWindow.closed) {
        authWindow.close()
        authWindow = null
      }
    }

    // Set up timeout handler
    timeoutId = setTimeout(() => {
      // Clean up resources
      cleanup()
      
      // Create timeout result
      const result: OAuth2Result = {
        success: false,
        error: {
          type: 'TIMEOUT',
          message: 'OAuth2 authorization timed out',
          details: 'The authorization process took too long to complete. Please try again.',
        },
      }
      
      // Call error callback if provided
      onError?.(
        result.error ??
        {
          type: 'UNKNOWN_ERROR',
          message: 'An unknown error occurred during OAuth2 authorization.',
        }
      )
      // Resolve with timeout result
      resolve(result)
    }, timeout)

    // Set up message listener for OAuth2 callback
    messageListener = (event: MessageEvent) => {
      // Optional: Verify origin for security
      // if (event.origin !== window.location.origin) return

      // Extract message data
      const { type, data, error } = event.data as { type?: string; data?: unknown; error?: unknown }

      // Handle successful authorization
      if (type === 'oauth2_success') {
        // Clean up resources
        cleanup()
        
        // Create success result
        const result: OAuth2Result = {
          success: true,
          data,
        }
        
        // Call success callback
        onSuccess?.(data)
        // Resolve with success
        resolve(result)
        
      // Handle authorization error
      } else if (type === 'oauth2_error') {
        // Clean up resources
        cleanup()
        
        // Create error result
        const errorObj = error as { type: string; message: string; details?: string } | undefined
        const result: OAuth2Result = {
          success: false,
          error: errorObj || {
            type: 'AUTHORIZATION_ERROR',
            message: 'OAuth2 authorization failed',
            details: 'The authorization process was not completed successfully.',
          },
        }
        
        // Call error callback
        onError?.(
          result.error ??
          {
            type: 'UNKNOWN_ERROR',
            message: 'An unknown error occurred during OAuth2 authorization.',
          }
        )
        // Resolve with error
        resolve(result)
      }
    }

    // Register message event listener
    window.addEventListener('message', messageListener)

    try {
      // Open OAuth2 authorization popup
      authWindow = window.open(
        authorizationUrl,
        'oauth2_auth',
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},` +
        'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes'
      )

      // Check if popup was blocked
      if (!authWindow) {
        // Clean up resources
        cleanup()
        
        // Create blocked result
        const result: OAuth2Result = {
          success: false,
          error: {
            type: 'POPUP_BLOCKED',
            message: 'Popup blocked by browser',
            details: 'Please allow popups for this site and try again.',
          },
        }
        
        // Call error callback
        onError?.(
          result.error ??
          {
            type: 'UNKNOWN_ERROR',
            message: 'An unknown error occurred during OAuth2 authorization.',
          }
        )
        // Resolve with blocked error
        resolve(result)
        return
      }

      // Monitor popup for manual closure
      const checkClosed = () => {
        // Check if popup was closed
        if (authWindow && authWindow.closed) {
          // Clean up resources
          cleanup()
          
          // Create cancelled result
          const result: OAuth2Result = {
            success: false,
            error: {
              type: 'USER_CANCELLED',
              message: 'Authorization cancelled',
              details: 'The authorization window was closed before completion.',
            },
          }
          
          // Call cancel callback
          onCancel?.()
          // Resolve with cancelled error
          resolve(result)
          return
        }
        
        // Continue monitoring if still open
        if (authWindow && !authWindow.closed) {
          setTimeout(checkClosed, 1000)
        }
      }
      
      // Begin popup monitoring
      setTimeout(checkClosed, 1000)

    } catch (error) {
      // Clean up on error
      cleanup()
      
      // Create error result
      const result: OAuth2Result = {
        success: false,
        error: {
          type: 'POPUP_ERROR',
          message: 'Failed to open authorization popup',
          details: error instanceof Error ? error.message : 'Unknown popup error',
        },
      }
      
      // Call error callback
      onError?.(
        result.error ??
        {
          type: 'UNKNOWN_ERROR',
          message: 'An unknown error occurred during OAuth2 authorization.',
        }
      )
      // Resolve with error
      resolve(result)
    }
  })
}

/**
 * Utility function to check if OAuth2 is supported by the current browser.
 * Verifies availability of required browser APIs for OAuth2 flow.
 * @returns True if OAuth2 popups are supported, false otherwise
 */
export function isOAuth2Supported(): boolean {
  try {
    // Verify required browser APIs
    return (
      typeof window !== 'undefined' &&
      typeof window.open === 'function' &&
      typeof window.postMessage === 'function' &&
      typeof window.addEventListener === 'function'
    )
  } catch {
    // Return false if any check fails
    return false
  }
}

/**
 * Utility function to generate a secure state parameter for OAuth2 flows.
 * Creates a URL-safe state string with embedded data and randomization.
 * @param data - Data to include in the state (will be JSON encoded)
 * @returns URL-safe base64 encoded state string
 */